// src/router/index.ts
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useLanguageStore } from '@/store/language';
import { useUserStore } from '@/store/user';

declare module 'vue-router' {
  interface RouteMeta {
    title?: (lang: string) => string
    adminOnly?: boolean
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/:lang(en|zh)',
    component: () => import('@/components/MainLayout.vue'),
    props: true,
    children: [
      {
        path: '',
        redirect: { name: 'home' }
      },
      {
        path: 'home',
        name: 'Home',
        component: () => import('@/views/HomeView.vue')
      },
      {
        path: 'news',
        name: 'News',
        component: () => import('@/views/NewsView.vue')
      },
      {
        path: 'news/:id',
        name: 'NewsDetail',
        component: () => import('@/views/NewsDetailView.vue'),
        props: true,
      },
      {
        path: 'showroom',
        name: 'Showroom',
        component: () => import('@/views/ShowroomView.vue')
      },
      {
        path: 'showroom/character/:id',
        name: 'ShowroomCharacterDetail',
        component: () => import('@/views/CharacterDetailView.vue'),
        props: true,
      },
      {
        path: 'quote',
        name: 'Quote',
        component: () => import('@/views/QuoteView.vue')
      },
      {
        path: 'drop',
        name: 'Drop',
        component: () => import('@/views/DropView.vue')
      },
      {
        path: 'console',
        component: () => import('@/components/ConsoleLayout.vue'),
        meta: {
          adminOnly: true
        },
        children: [
          {
            path: '',
            redirect: { name: 'ConsoleUser' }
          },
          {
            path: 'user',
            name: 'ConsoleUser',
            component: () => import('@/views/ConsoleUserView.vue')
          },
          {
            path: 'news',
            name: 'ConsoleNews',
            component: () => import('@/views/ConsoleNewsView.vue')
          },
          {
            path: 'news/:id',
            name: 'ConsoleNewsDetail',
            component: () => import('@/views/NewsDetailView.vue'),
            props: true,
          },
          {
            path: 'label/:category',
            name: 'ConsoleLabel',
            component: () => import('@/views/ConsoleLabelView.vue')
          },
          {
            path: 'character/:creatorRole',
            name: 'ConsoleCharacter',
            component: () => import('@/views/ConsoleCharacterView.vue')
          },
          {
            path: 'quote',
            name: 'ConsoleQuote',
            component: () => import('@/views/ConsoleQuoteView.vue')
          },
          {
            path: 'drop',
            name: 'ConsoleDrop',
            component: () => import('@/views/ConsoleDropView.vue')
          }
        ]
      },
      {
        path: 'personal-center',
        name: 'PersonalCenter',
        component: () => import('@/views/PersonalCenter.vue')
      }
    ]
  },
  {
    path: '/',
    redirect: () => {
      const languageStore = useLanguageStore();
      return `/${languageStore.language || 'en'}/home`
    }
  },
  {
    path: '/console',
    redirect: () => {
      const languageStore = useLanguageStore();
      return `/${languageStore.language || 'en'}/console/news`
    }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: () => {
      const languageStore = useLanguageStore();
      return `/${languageStore.language || 'en'}/home`
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL || '/'),
  routes
})

router.beforeEach(async (to) => {
  const languageStore = useLanguageStore()
  const userStore = useUserStore()

  // Check admin privileges for admin-only routes
  if (to.meta.adminOnly && userStore.role !== 'admin') {
    return `/${languageStore.language || 'en'}/home`
  }

  // Handle root path redirection
  if (to.path === '/') {
    await languageStore.detectLanguage()
    return `/${languageStore.language}/home`
  }

  // Handle console root path redirection
  if (to.path === '/console') {
    await languageStore.detectLanguage()
    return `/${languageStore.language}/console/news`
  }

  // Sync language parameter with store
  if (to.params.lang && to.params.lang !== languageStore.language) {
    languageStore.setLanguage(to.params.lang as 'en' | 'zh')
  }

  return true
})

router.afterEach(() => {
  window.scrollTo(0, 0)
})

export default router